/**
 * Shared bits for the Check-In tab: tier/urgency/cluster colour scales and
 * small formatting helpers.
 *
 * The priority scale (AAA dark green → C red) and the RAG index lights are
 * deliberate traffic-lights — they sit outside the brand book's
 * monochrome-plus-Trusted-Blue rule because the check-in meeting reads
 * red/amber/green faster than any brand-safe palette.
 */
import type { Flag, Rag } from '../../../utils/taCheckinIndex';

export const TIER_ORDER = ['AAA', 'A', 'B', 'C', 'Unrated'] as const;
export const TIER_COLOR: Record<string, string> = {
  AAA: '#15803d',
  A: '#86efac',
  B: '#f97316',
  C: '#dc2626',
  Unrated: '#9ca3af',
};
export const TIER_TEXT: Record<string, string> = {
  AAA: '#fff',
  A: '#14532d',
  B: '#fff',
  C: '#fff',
  Unrated: '#fff',
};

export const URG_ORDER = ['urgent', 'stretch', 'in time'] as const;
export const URG_LABEL: Record<string, string> = {
  urgent: 'Urgent',
  stretch: 'Stretch',
  'in time': 'In time',
  __none__: 'Not set',
};
export const URG_STYLE: Record<string, { bg: string; fg: string }> = {
  urgent: { bg: '#fcdfe4', fg: '#a3182f' },
  stretch: { bg: '#fdf0dc', fg: '#8a5a12' },
  'in time': { bg: '#e4f3e9', fg: '#1d6b39' },
  __none__: { bg: '#f0f0ec', fg: '#6b6b6b' },
};

export const DEPT_COLORS: Record<string, { bg: string; fg: string }> = {
  Growth: { bg: '#fee2e2', fg: '#b91c1c' },
  Operations: { bg: '#fef3c7', fg: '#92400e' },
  'Finance & Legal': { bg: '#dcfce7', fg: '#166534' },
  Fleet: { bg: '#dbeafe', fg: '#1e40af' },
  People: { bg: '#cffafe', fg: '#0e7490' },
  Tech: { bg: '#f3e8ff', fg: '#7e22ce' },
  'Core Functions': { bg: '#ede9fe', fg: '#5b21b6' },
};
export const DEFAULT_DEPT_COLOR = { bg: '#f0f0ec', fg: '#6b6b6b' };

export const RAG_COLOR: Record<Rag, string> = { red: '#dc2626', amber: '#e88f3d', green: '#26bc58' };
export const FLAG_COLOR: Record<Flag, string> = {
  crit: '#dc2626',
  watch: '#e88f3d',
  ok: '#26bc58',
  none: '#d3d1c7',
};

export const tierRank = (t: string) => {
  const i = TIER_ORDER.indexOf(t as (typeof TIER_ORDER)[number]);
  return i === -1 ? TIER_ORDER.length : i;
};
export const urgRank = (u: string) => {
  const i = URG_ORDER.indexOf(u as (typeof URG_ORDER)[number]);
  return i === -1 ? URG_ORDER.length : i;
};

export const round1 = (n: number) => Math.round(n * 10) / 10;

export function fmtDate(s: string): string {
  if (!s) return '';
  const d = new Date(s + 'T00:00:00');
  return isNaN(d.getTime())
    ? s
    : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Days from `iso` to today; positive = overdue. */
export function daysBetween(iso: string): number | null {
  if (!iso) return null;
  const d = new Date(iso + 'T00:00:00');
  if (isNaN(d.getTime())) return null;
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  return Math.round((t.getTime() - d.getTime()) / 86400000);
}

export function TierBadge({ tier }: { tier: string }) {
  return (
    <span
      className="inline-block rounded px-2 py-0.5 text-[11px] font-bold tracking-wide"
      style={{ background: TIER_COLOR[tier] ?? TIER_COLOR.Unrated, color: TIER_TEXT[tier] ?? '#fff' }}
    >
      {tier === 'Unrated' ? 'UNRATED' : tier}
    </span>
  );
}

export function UrgencyChip({ urgency }: { urgency: string }) {
  const key = (URG_ORDER as readonly string[]).includes(urgency) ? urgency : '__none__';
  if (key === '__none__') return <span className="text-gray-400">—</span>;
  const s = URG_STYLE[key];
  return (
    <span className="inline-block rounded px-2 py-0.5 text-xs" style={{ background: s.bg, color: s.fg }}>
      {URG_LABEL[key].toLowerCase()}
    </span>
  );
}

export function ClusterChip({ name }: { name: string }) {
  const c = DEPT_COLORS[name] || DEFAULT_DEPT_COLOR;
  return (
    <span className="inline-block rounded px-2 py-0.5 text-xs" style={{ background: c.bg, color: c.fg }}>
      {name}
    </span>
  );
}
